import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { DealService, CargoDealDetail } from '../../../services/api/deal';
import { CargoRequestService } from '../../../services/api/cargo-request';
import { LanguageService } from '../../../services/i18n/language';
import { DocNumberPipe } from '../../../services/request-number';
import { isDeliveredByCodes, visibleActivities } from '../../../services/deal-stage';
import { paymentSummary, CurrencyTotals } from '../../../services/deal-payments';
import { HomeCurrencyService, fmtMoney, sumIn, othersText, totalsText, mergeTotals } from '../../../services/home-currency';

interface DealRow {
  id: number;
  createdAt: string;
  route: string;
  client: string;
  stage: string;
  updatedToday: boolean;
  /** Груз передан экспедитору (забор / склад экспедитора / в пути). */
  handedOver: boolean;
  delivered: boolean;
  weightKg: number;
  price: number;
  currency: string;
  /** Цена в тенге, если её посчитал сервер (по курсу на сегодня). */
  priceKzt: number | null;
  /** Счета и оплаты по валютам. */
  totals: CurrencyTotals[];
}

/** Этапы, после которых груз считается переданным (как в счётчике на странице сделки). */
const HANDOVER_CODES = ['CARGO_PICKUP_DONE', 'CARGO_AT_FORWARDER_WAREHOUSE', 'IN_TRANSIT'];

/**
 * «Сводка» кабинета экспедитора: главное на сегодня — сколько сделок в работе, где не обновлён статус,
 * ставки, ждущие решения, и новые запросы. Всё считается из тех же запросов, что и разделы кабинета.
 */
@Component({
  selector: 'app-ff-overview',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe],
  templateUrl: './ff-overview.component.html',
  styleUrl: './ff-overview.component.scss'
})
export class FfOverviewComponent implements OnInit, OnDestroy {
  loading = true;
  bids: FreightForwarderBidDto[] = [];

  /** Запросы, которых больше нет (удалены или отменены) — ставки по ним не считаем. */
  goneRequests = new Set<number>();
  deals: DealRow[] = [];
  openRequests: number | null = null;

  private subscriptions = new Subscription();

  // ─── Деньги — в валюте страны компании; счета в других валютах не пересчитываются ───

  /** Валюта сводок — по стране компании (₸ для Казахстана, € для Европы…). */
  currency = 'KZT';

  /** Сумма в валюте компании. */
  home(v: number): string {
    return fmtMoney(v, this.currency, this.languageService.getCurrentLanguageValue());
  }

  /** Итог по набору сделок: в валюте компании, а если в ней ничего нет — в других валютах. */
  homeSum(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    const v = sumIn(rows, this.currency, field);
    return v > 0 ? this.home(v) : this.dealTotals(mergeTotals(rows), field);
  }

  /** Суммы в других валютах — «+ 3 000 USD»; пусто, если их нет или итог уже показан в них. */
  homeOthers(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    if (!(sumIn(rows, this.currency, field) > 0)) return '';
    return othersText(rows, this.currency, field, this.languageService.getCurrentLanguageValue());
  }

  /** Поле по сделке (или нескольким) во всех валютах, своя первой; пусто — «0» в своей. */
  dealTotals(totals: CurrencyTotals[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    return totalsText(totals, this.currency, field, this.languageService.getCurrentLanguageValue()) || this.home(0);
  }

  constructor(
    private bidService: FreightForwarderBidService,
    private dealService: DealService,
    private cargoRequestService: CargoRequestService,
    private homeCurrencyService: HomeCurrencyService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.subscriptions.add(this.homeCurrencyService.currency$.subscribe(c => {
      this.currency = c;
      this.cdr.markForCheck();
    }));

    const bids$ = this.bidService.getAllBidsPaginated(0, 200).pipe(catchError(() => of(null)));
    const deals$ = this.dealService.getMyDealsForwarderPaginated(0, 50).pipe(catchError(() => of(null)));
    const requests$ = this.cargoRequestService.searchCargoRequests({ status: 'PENDING' } as any, 0, 1).pipe(catchError(() => of(null)));

    this.subscriptions.add(
      forkJoin([bids$, deals$, requests$]).subscribe(([bids, deals, requests]) => {
        this.bids = bids?.content ?? [];
        this.checkRequests();
        this.openRequests = requests?.totalElements ?? null;
        const list = deals?.content ?? [];
        if (!list.length) {
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }
        // Этап и маршрут есть только в карточке сделки
        this.subscriptions.add(
          forkJoin(list.map(d => this.dealService.getDealDetail(d.id).pipe(catchError(() => of(null))))).subscribe(details => {
            this.deals = details.filter((d): d is CargoDealDetail => !!d).map(d => this.toRow(d));
            this.loading = false;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  /** Проверяет запросы по ставкам, которые ещё ждут решения: удалённые или отменённые убираем. */
  private checkRequests(): void {
    const ids = [...new Set(this.bids.filter(b => this.isPending(b)).map(b => b.cargoRequestId))].filter(Boolean);
    if (!ids.length) return;
    this.subscriptions.add(
      forkJoin(ids.map(id => this.cargoRequestService.getCargoRequest(id).pipe(catchError(() => of(null))))).subscribe(list => {
        list.forEach((r, i) => {
          const raw = r as any;
          const status = String(raw?.status ?? raw?.requestStatus ?? '').toUpperCase();
          if (!r || raw.deleted === true || raw.isDeleted === true || ['CANCELLED', 'CANCELED', 'DELETED'].includes(status)) {
            this.goneRequests.add(ids[i]);
          }
        });
        this.cdr.markForCheck();
      })
    );
  }

  private isPending(b: FreightForwarderBidDto): boolean {
    return b.bidStatus !== 'ACCEPTED' && b.bidStatus !== 'LOST';
  }

  /** Ставка по запросу, который удалён или отменён. */
  private isGone(b: FreightForwarderBidDto): boolean {
    return this.isPending(b) && this.goneRequests.has(b.cargoRequestId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private toRow(d: CargoDealDetail): DealRow {
    const lang = this.languageService.getCurrentLanguageValue();
    const statuses = visibleActivities(d.activities, 'ROLE_FREIGHT_FORWARDER').filter(a => a.activityType?.category === 'STATUS');
    const last = [...statuses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const codes = statuses.map(a => a.activityType?.code);
    const r = d.cargoRequest;
    return {
      id: d.id,
      createdAt: d.createdAt,
      route: `${r?.fromCity ?? ''} → ${r?.toCity ?? ''}`,
      client: d.cargoOwner?.companyName ?? '',
      stage: last ? ((lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '—') : '—',
      updatedToday: !!last && new Date(last.createdAt).toDateString() === new Date().toDateString(),
      handedOver: codes.some(c => HANDOVER_CODES.includes(c)),
      delivered: isDeliveredByCodes(codes),
      weightKg: (r?.products ?? []).reduce((sum, p) => sum + (p.totalWeightKg || 0), 0),
      price: d.bid?.price || 0,
      currency: d.bid?.currencyCode || '',
      priceKzt: d.bid?.priceKzt ?? null,
      totals: paymentSummary(d.activities).totals,
    };
  }

  // ─── Цифры для плиток ───

  /** Сделки в работе — пока груз не доставлен. */
  get activeDeals(): DealRow[] {
    return this.deals.filter(d => !d.delivered);
  }

  // ─── Итоги ───

  get deliveredCount(): number {
    return this.deals.filter(d => d.delivered).length;
  }

  get inTransitCount(): number {
    return this.deals.filter(d => d.handedOver && !d.delivered).length;
  }

  get preparingCount(): number {
    return this.deals.filter(d => !d.handedOver && !d.delivered).length;
  }

  /** Вес только доставленных грузов. */
  get totalWeightText(): string {
    const kg = this.deals.filter(d => d.delivered).reduce((sum, d) => sum + d.weightKg, 0);
    return this.number(kg) + ' ' + this.t('ffSearch.kg', 'кг');
  }

  /** Стоимость перевозок — по выставленным счетам в валюте компании; счета в других валютах — отдельной строкой. */
  get totalFreightText(): string {
    return this.homeSum(this.deals, 'invoiced');
  }

  get totalFreightOthers(): string {
    return this.homeOthers(this.deals, 'invoiced');
  }

  private number(n: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n || 0);
  }

  /** Требуют внимания: статус сегодня не обновлялся. */
  get needUpdate(): DealRow[] {
    return this.activeDeals.filter(d => !d.updatedToday);
  }

  /** Требуют внимания: клиент не оплатил выставленные счета. */
  get withDebt(): DealRow[] {
    return this.deals.filter(d => d.totals.some(t => t.debt > 0));
  }

  /** Требуют внимания: клиент заплатил больше выставленного — зачесть в следующий счёт или вернуть. */
  get withOverpay(): DealRow[] {
    return this.deals.filter(d => d.totals.some(t => t.overpaid > 0));
  }

  /** Есть ли что-то в «Требует внимания». */
  get hasAttention(): boolean {
    return this.needUpdate.length > 0 || this.withDebt.length > 0 || this.withOverpay.length > 0;
  }

  kzt(v: number): string {
    return this.number(Math.round(v)) + ' ₸';
  }

  get pendingBids(): number {
    return this.bids.filter(b => this.isPending(b) && !this.isGone(b)).length;
  }

  get acceptedBids(): number {
    return this.bids.filter(b => b.bidStatus === 'ACCEPTED').length;
  }

  /** Ставки, по которым клиент уже принял решение (выбрал или не выбрал). */
  get decidedBids(): number {
    return this.bids.filter(b => b.bidStatus === 'ACCEPTED' || b.bidStatus === 'LOST').length;
  }

  /** Доля выигранных ставок среди тех, по которым клиент принял решение. */
  get winRate(): number | null {
    const decided = this.decidedBids;
    return decided ? Math.round((this.acceptedBids / decided) * 100) : null;
  }

  get latestBids(): FreightForwarderBidDto[] {
    return [...this.bids]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 5);
  }

  bidStatusText(b: FreightForwarderBidDto): string {
    if (b.bidStatus === 'ACCEPTED') return this.t('freightForwarder.dashboard.accepted', 'Принята');
    if (b.bidStatus === 'LOST') return this.t('ffDash.statusLost', 'Не выбрана');
    return this.t('ffDash.statusSent', 'Отправлена');
  }

  bidTone(b: FreightForwarderBidDto): string {
    return b.bidStatus === 'ACCEPTED' ? 'green' : b.bidStatus === 'LOST' ? 'red' : 'blue';
  }

  money(amount: number, code: string): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount || 0)} ${code || ''}`.trim();
  }
}
